import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Target } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TradeInQuoteButton } from './TradeInQuoteButton';

const TONE = { New: 'bg-red-100 text-red-800', Contacted: 'bg-amber-100 text-amber-800', Won: 'bg-emerald-100 text-emerald-800', Lost: 'bg-slate-200 text-slate-600' };

// Status + note editor for one trade-in lead (used on the report and on repair orders).
export const LeadStatusControl = ({ lead, onSaved }) => {
  const { api } = useAuth();
  const [status, setStatus] = useState(lead.status || 'New');
  const [notes, setNotes] = useState(lead.notes || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setStatus(lead.status || 'New'); setNotes(lead.notes || ''); }, [lead.status, lead.notes]);
  const dirty = status !== (lead.status || 'New') || notes !== (lead.notes || '');
  const save = async () => {
    setSaving(true);
    try { await api().put(`/trade-in-leads/${encodeURIComponent(lead.key)}`, { status, notes }); toast.success(`Lead marked ${status}`); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className={`w-32 h-8 rounded-sm text-xs font-semibold ${TONE[status]}`} data-testid={`lead-status-${lead.key}`}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.keys(TONE).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Follow-up note" className="h-8 text-xs rounded-sm flex-1" data-testid={`lead-notes-${lead.key}`} />
      <Button size="sm" disabled={!dirty || saving} onClick={save} className="h-8 rounded-sm bg-slate-900" data-testid={`lead-save-${lead.key}`}>Save</Button>
      <TradeInQuoteButton lead={lead} />
    </div>
  );
};

export const TradeInLeadBanner = ({ roId }) => {
  const { api } = useAuth();
  const [lead, setLead] = useState(null);
  const load = () => api().get(`/trade-in-leads/for-ro/${roId}`).then(r => setLead(r.data)).catch(() => {});
  useEffect(() => { if (roId) load(); }, [roId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!lead?.is_lead) return null;
  return (
    <div className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 flex items-center gap-4 flex-wrap" data-testid="trade-in-lead-banner">
      <div className="flex items-center gap-2 text-sm text-red-900">
        <Target className="w-4 h-4 text-red-600" />
        <span><b>Trade-in lead</b>: this customer runs a {lead.brands.join(' / ')}. A Lyphline upgrade opportunity.</span>
        <Link to="/reports/competitor-repairs" className="text-xs underline text-red-700">All leads</Link>
      </div>
      <div className="flex-1 min-w-[320px]"><LeadStatusControl lead={lead} onSaved={load} /></div>
    </div>
  );
};
